import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  Settings, Save, AlertTriangle, Check, ToggleLeft, ToggleRight
} from 'lucide-react';

const SuperAdminConfig = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/super-admin/config');
        setConfig(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchConfig();
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/super-admin/config', config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert(err.response?.data?.detail || t('saConfig.error')); }
    finally { setSaving(false); }
  };

  const handleToggleMaintenance = async () => {
    const newVal = !config.maintenance_mode;
    if (newVal && !window.confirm(t('saConfig.confirmMaintenance'))) return;
    try {
      await api.post('/super-admin/maintenance', { enabled: newVal });
      setConfig(prev => ({ ...prev, maintenance_mode: newVal }));
    } catch (err) { alert(err.response?.data?.detail || t('saConfig.error')); }
  };

  const toggleFeature = (key) => {
    setConfig(prev => ({
      ...prev,
      features_enabled: { ...prev.features_enabled, [key]: !prev.features_enabled?.[key] }
    }));
  };

  if (loading || !config) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  const plans = config.plans || {};

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="super-admin-config">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{t('saConfig.title')}</h1>
            <p className="text-sm text-gray-500">{t('saConfig.subtitle')}</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-config-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344] disabled:opacity-50 transition-all"
          >
            {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saved ? t('saConfig.saved') : saving ? t('saConfig.saving') : t('saConfig.save')}
          </button>
        </div>

        {/* Maintenance Mode */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${config.maintenance_mode ? 'text-amber-400' : 'text-gray-500'}`} />
              <div>
                <h3 className="text-sm font-semibold text-white">{t('saConfig.maintenanceTitle')}</h3>
                <p className="text-xs text-gray-500">{t('saConfig.maintenanceDesc')}</p>
              </div>
            </div>
            <button onClick={handleToggleMaintenance} data-testid="toggle-maintenance" className="p-1">
              {config.maintenance_mode ? (
                <ToggleRight className="w-10 h-10 text-amber-400" />
              ) : (
                <ToggleLeft className="w-10 h-10 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* General Settings */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-white">{t('saConfig.generalSettings')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('saConfig.platformName')}</label>
              <input
                value={config.platform_name || ''}
                onChange={e => setConfig(prev => ({ ...prev, platform_name: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('saConfig.gracePeriod')}</label>
              <input
                type="number" min="0" max="30"
                value={config.grace_period_days || 5}
                onChange={e => setConfig(prev => ({ ...prev, grace_period_days: parseInt(e.target.value) || 5 }))}
                className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40"
              />
            </div>
          </div>
        </div>

        {/* Plans */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-white">{t('saConfig.plans')}</h3>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(plans).map(([key, plan]) => (
              <div key={key} className="bg-[#0a0f1c] rounded-xl p-4 border border-white/5">
                <h4 className="text-xs font-bold text-white uppercase mb-3">{plan.name}</h4>
                <div className="space-y-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">{t('saConfig.planPrice')}</label>
                    <input
                      type="number" step="0.01" value={plan.price}
                      onChange={e => setConfig(prev => ({
                        ...prev,
                        plans: { ...prev.plans, [key]: { ...prev.plans[key], price: parseFloat(e.target.value) || 0 } }
                      }))}
                      className="w-full px-2 py-1.5 bg-[#111827] border border-white/10 rounded text-sm text-white focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">{t('saConfig.maxShops')}</label>
                    <input
                      type="number" min="1" value={plan.max_shops}
                      onChange={e => setConfig(prev => ({
                        ...prev,
                        plans: { ...prev.plans, [key]: { ...prev.plans[key], max_shops: parseInt(e.target.value) || 1 } }
                      }))}
                      className="w-full px-2 py-1.5 bg-[#111827] border border-white/10 rounded text-sm text-white focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">{t('saConfig.maxUsers')}</label>
                    <input
                      type="number" min="1" value={plan.max_users}
                      onChange={e => setConfig(prev => ({
                        ...prev,
                        plans: { ...prev.plans, [key]: { ...prev.plans[key], max_users: parseInt(e.target.value) || 1 } }
                      }))}
                      className="w-full px-2 py-1.5 bg-[#111827] border border-white/10 rounded text-sm text-white focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-white">{t('saConfig.features')}</h3>
          {Object.entries(config.features_enabled || {}).map(([key, enabled]) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <span className="text-sm text-gray-300 capitalize">{key.replace(/_/g, ' ')}</span>
              <button onClick={() => toggleFeature(key)} className="p-0.5">
                {enabled ? (
                  <ToggleRight className="w-8 h-8 text-[#009b3a]" />
                ) : (
                  <ToggleLeft className="w-8 h-8 text-gray-600" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminConfig;
